import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
export type TicketPriority = "low" | "normal" | "high" | "urgent";
export type TicketChannel = "learner_centre" | "centre_platform";

export interface HelpdeskTicket {
  id: string;
  tenant_id: string;
  subject: string;
  body: string;
  status: TicketStatus;
  priority: TicketPriority;
  channel: TicketChannel;
  requester_name: string;
  requester_email: string | null;
  requester_user_id: string | null;
  student_id: string | null;
  assigned_to: string | null;
  resolved_at: string | null;
  created_by: string | null;
  last_message_at: string | null;
  message_count: number;
  requester_unread_count: number;
  responder_unread_count: number;
  requester_last_read_at: string | null;
  responder_last_read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface HelpdeskTicketListResponse {
  items: HelpdeskTicket[];
  total: number;
}

export interface CreateHelpdeskTicketInput {
  subject: string;
  body: string;
  priority?: TicketPriority;
  channel?: TicketChannel;
  requester_name: string;
  requester_email?: string | null;
  student_id?: string | null;
}

export interface UpdateHelpdeskTicketInput {
  status?: TicketStatus;
  priority?: TicketPriority;
  assigned_to?: string | null;
}

export interface HelpdeskAttachment {
  filename: string;
  content_type: string;
  size_bytes: number;
  object_path: string;
}

export interface HelpdeskMessage {
  id: string;
  ticket_id: string;
  author_user_id: string;
  author_role: string;
  author_name: string;
  body: string;
  attachments: HelpdeskAttachment[];
  client_message_id: string | null;
  delivery_status: "sending" | "delivered";
  moderation_status: "visible" | "held" | "removed";
  created_at: string;
}

export interface HelpdeskMessageListResponse {
  items: HelpdeskMessage[];
  total: number;
}

function helpdeskWebsocketUrl(ticketId: string): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  const origin = configured?.startsWith("http")
    ? new URL(configured).origin
    : window.location.origin;
  const url = new URL(`/api/helpdesk/tickets/${ticketId}/ws`, origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

/** Map ticket status → StatusPill variant for DataTable rows. */
export const TICKET_STATUS_PILL: Record<
  TicketStatus,
  "active" | "invited" | "inactive" | "archived"
> = {
  open: "active",
  in_progress: "invited",
  resolved: "inactive",
  closed: "archived",
};

export function useHelpdeskTickets(params?: {
  status?: TicketStatus;
  channel?: TicketChannel;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.channel) qs.set("channel", params.channel);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return useQuery<HelpdeskTicketListResponse>({
    queryKey: ["helpdesk-tickets", params],
    queryFn: () => api.get<HelpdeskTicketListResponse>(`/api/helpdesk/tickets${suffix}`),
  });
}

export function useHelpdeskMessages(ticketId: string | null) {
  return useQuery<HelpdeskMessageListResponse>({
    queryKey: ["helpdesk-messages", ticketId],
    queryFn: () =>
      api.get<HelpdeskMessageListResponse>(`/api/helpdesk/tickets/${ticketId}/messages`),
    enabled: ticketId !== null,
    refetchInterval: ticketId === null ? false : 5000,
  });
}

export function useHelpdeskRealtime(ticketId: string | null) {
  const qc = useQueryClient();
  React.useEffect(() => {
    if (!ticketId) return;
    const firstToken = api.getAccessToken();
    if (!firstToken) return;
    let stopped = false;
    let socket: WebSocket | null = null;
    let retry = 800;
    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(helpdeskWebsocketUrl(ticketId), [
        "bearer",
        api.getAccessToken() ?? firstToken,
      ]);
      socket.onopen = () => {
        retry = 800;
      };
      socket.onmessage = (event) => {
        try {
          const frame = JSON.parse(String(event.data)) as { type?: string };
          if (frame.type !== "helpdesk.message.created") return;
          void qc.invalidateQueries({ queryKey: ["helpdesk-messages", ticketId] });
          void qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
        } catch {
          // Ignore malformed transport frames; polling remains the fallback.
        }
      };
      socket.onclose = () => {
        if (stopped) return;
        window.setTimeout(connect, retry);
        retry = Math.min(retry * 1.6, 8000);
      };
    };
    connect();
    return () => {
      stopped = true;
      socket?.close();
    };
  }, [qc, ticketId]);
}

export function useCreateHelpdeskMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ticketId,
      body,
      clientMessageId,
      attachments,
    }: {
      ticketId: string;
      body: string;
      clientMessageId: string;
      attachments?: HelpdeskAttachment[];
    }) =>
      api.post<HelpdeskMessage>(`/api/helpdesk/tickets/${ticketId}/messages`, {
        body,
        client_message_id: clientMessageId,
        attachments: attachments ?? [],
      }),
    onMutate: async (variables) => {
      const queryKey = ["helpdesk-messages", variables.ticketId] as const;
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<HelpdeskMessageListResponse>(queryKey);
      const optimistic: HelpdeskMessage = {
        id: `optimistic:${variables.clientMessageId}`,
        ticket_id: variables.ticketId,
        author_user_id: "current-user",
        author_role: "staff",
        author_name: "You",
        body: variables.body,
        attachments: variables.attachments ?? [],
        client_message_id: variables.clientMessageId,
        delivery_status: "sending",
        moderation_status: "visible",
        created_at: new Date().toISOString(),
      };
      qc.setQueryData<HelpdeskMessageListResponse>(queryKey, (current) => ({
        items: [...(current?.items ?? []), optimistic],
        total: (current?.total ?? 0) + 1,
      }));
      return { previous, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        qc.setQueryData(context.queryKey, context.previous ?? { items: [], total: 0 });
      }
    },
    onSuccess: (message, variables) => {
      qc.setQueryData<HelpdeskMessageListResponse>(
        ["helpdesk-messages", variables.ticketId],
        (current) => ({
          items: (current?.items ?? []).map((item) =>
            item.id === `optimistic:${variables.clientMessageId}` ? message : item,
          ),
          total: current?.total ?? 1,
        }),
      );
      void qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
    },
  });
}

export function useMarkHelpdeskRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) =>
      api.post<HelpdeskTicket>(`/api/helpdesk/tickets/${ticketId}/read`, {}),
    onSuccess: (_ticket, ticketId) => {
      void qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
      void qc.invalidateQueries({ queryKey: ["helpdesk-messages", ticketId] });
    },
  });
}

export function useCreateHelpdeskTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateHelpdeskTicketInput) =>
      api.post<HelpdeskTicket>("/api/helpdesk/tickets", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
    },
  });
}

export function useUpdateHelpdeskTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateHelpdeskTicketInput & { id: string }) =>
      api.patch<HelpdeskTicket>(`/api/helpdesk/tickets/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
    },
  });
}

const CHAT_FILE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

export async function uploadHelpdeskFile(
  ticketId: string,
  file: File,
): Promise<HelpdeskAttachment> {
  if (!CHAT_FILE_TYPES.has(file.type) || file.size > 8 * 1024 * 1024 || file.size < 1) {
    throw new Error("Use an image, PDF, text, Word, or Excel file under 8 MB.");
  }
  const signed = await api.post<{
    put_url: string;
    object_path: string;
    required_headers: Record<string, string>;
  }>(`/api/helpdesk/tickets/${ticketId}/uploads`, {
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
  });
  const target = signed.put_url.startsWith("http")
    ? signed.put_url
    : new URL(signed.put_url, import.meta.env.VITE_API_URL || window.location.origin).toString();
  const uploaded = await fetch(target, {
    method: "PUT",
    headers: signed.required_headers,
    body: file,
  });
  if (!uploaded.ok) {
    throw new Error("The file could not be uploaded.");
  }
  return {
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
    object_path: signed.object_path,
  };
}

export async function openHelpdeskFile(ticketId: string, objectPath: string): Promise<void> {
  const signed = await api.get<{ get_url: string }>(
    `/api/helpdesk/tickets/${ticketId}/downloads?object_path=${encodeURIComponent(objectPath)}`,
  );
  window.open(signed.get_url, "_blank", "noopener,noreferrer");
}
